import React, { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { personalInfo } from '../data/mock';
import { Mail, MapPin, Github, Linkedin, Send, Sparkles, MessageCircle, User, AtSign } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Contact = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const formRef = useRef(null);
  const infoRef = useRef(null);
  const { currentColors } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Enhanced title animation
            anime({
              targets: titleRef.current,
              opacity: [0, 1],
              translateY: [80, 0],
              rotateX: [90, 0],
              scale: [0.8, 1.1, 1],
              duration: 1200,
              easing: 'easeOutElastic(1, .6)'
            });

            // Form animation with 3D effects
            anime({
              targets: formRef.current,
              opacity: [0, 1],
              translateX: [-60, 0],
              rotateY: [30, 0],
              duration: 1000,
              delay: 400,
              easing: 'easeOutQuart'
            });

            // Info animation with 3D effects
            anime({
              targets: infoRef.current,
              opacity: [0, 1],
              translateX: [60, 0],
              rotateY: [-30, 0],
              duration: 1000,
              delay: 600,
              easing: 'easeOutQuart'
            });

            // Animate contact info items
            setTimeout(() => {
              const infoItems = infoRef.current.querySelectorAll('.contact-item');
              anime({
                targets: infoItems,
                opacity: [0, 1],
                translateX: [30, 0],
                scale: [0.9, 1],
                duration: 600,
                delay: anime.stagger(150, { start: 1000 }),
                easing: 'easeOutQuart'
              });
            }, 100);

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Enhanced form submission animation
    anime({
      targets: '.submit-btn',
      scale: [1, 0.95, 1.05, 1],
      rotateZ: [0, -5, 5, 0],
      duration: 500,
      easing: 'easeOutQuart'
    });

    try {
      // Send message to Formspree
      const response = await fetch('https://formspree.io/f/mzzdoepp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Success animation
      anime({
        targets: formRef.current,
        scale: [1, 1.02, 1],
        duration: 300,
        easing: 'easeOutQuad',
        complete: () => {
          alert('✨ Message sent successfully! Thank you for reaching out. I\'ll get back to you soon!');
          setFormData({ name: '', email: '', subject: '', message: '' });
          setIsSubmitting(false);
        }
      });
    } catch (error) {
      console.error('Error sending message:', error);
      anime({
        targets: formRef.current,
        translateX: [0, -10, 10, -10, 10, 0],
        duration: 500,
        easing: 'easeOutQuart',
        complete: () => {
          alert('❌ Failed to send message. Please try again or contact me directly via email.');
          setIsSubmitting(false);
        }
      });
    }
  };

  const socialLinks = [
    { icon: <Github size={24} />, url: personalInfo.social.github, label: 'GitHub', color: '#333' },
    { icon: <Linkedin size={24} />, url: personalInfo.social.linkedin, label: 'LinkedIn', color: '#0077B5' }
  ];

  return (
    <section 
      id="contact" 
      ref={sectionRef} 
      className="py-16 md:py-32 relative z-10 transition-colors duration-500"
      style={{ backgroundColor: currentColors.bg }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <h2 
          ref={titleRef}
          className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-center mb-20 opacity-0 relative font-display"
          style={{ 
            color: currentColors.text
          }}
        >
          Let's Connect
          <div 
            className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-1 w-32 opacity-80"
            style={{ backgroundColor: currentColors.accent }}
          />
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto">
          {/* Enhanced Contact Form */}
          <div ref={formRef} className="opacity-0">
            <div className="flex items-center gap-3 mb-8">
              <MessageCircle size={24} style={{ color: currentColors.accent }} />
              <h3 
                className="font-mono text-lg uppercase tracking-wide font-bold"
                style={{ color: currentColors.text }}
              >
                Send a Message
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label 
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide font-semibold mb-3"
                    style={{ color: currentColors.textSecondary }}
                  >
                    <User size={14} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 md:py-4 rounded-lg font-medium transition-all duration-300 focus:outline-none"
                    style={{
                      backgroundColor: currentColors.cardBg,
                      color: currentColors.text,
                      border: `2px solid ${currentColors.borderLight}`
                    }}
                    placeholder="Your full name"
                    onFocus={(e) => e.target.style.borderColor = currentColors.accent}
                    onBlur={(e) => e.target.style.borderColor = currentColors.borderLight}
                  />
                </div>
                <div>
                  <label 
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide font-semibold mb-3"
                    style={{ color: currentColors.textSecondary }}
                  >
                    <AtSign size={14} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 md:py-4 rounded-lg font-medium transition-all duration-300 focus:outline-none"
                    style={{
                      backgroundColor: currentColors.cardBg,
                      color: currentColors.text,
                      border: `2px solid ${currentColors.borderLight}`
                    }}
                    placeholder="your@email.com"
                    onFocus={(e) => e.target.style.borderColor = currentColors.accent}
                    onBlur={(e) => e.target.style.borderColor = currentColors.borderLight}
                  />
                </div>
              </div>

              <div>
                <label 
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide font-semibold mb-3"
                  style={{ color: currentColors.textSecondary }}
                >
                  <Sparkles size={14} />
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 md:py-4 rounded-lg font-medium transition-all duration-300 focus:outline-none"
                  style={{
                    backgroundColor: currentColors.cardBg,
                    color: currentColors.text,
                    border: `2px solid ${currentColors.borderLight}`
                  }}
                  placeholder="What's this about?"
                  onFocus={(e) => e.target.style.borderColor = currentColors.accent}
                  onBlur={(e) => e.target.style.borderColor = currentColors.borderLight}
                />
              </div>

              <div>
                <label 
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide font-semibold mb-3"
                  style={{ color: currentColors.textSecondary }}
                >
                  <MessageCircle size={14} />
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 md:py-4 rounded-lg font-medium transition-all duration-300 resize-none focus:outline-none message-textarea"
                  style={{
                    backgroundColor: currentColors.cardBg,
                    color: currentColors.text,
                    border: `2px solid ${currentColors.borderLight}`,
                    WebkitTextFillColor: currentColors.text
                  }}
                  placeholder="Tell me about your project or just say hello..."
                  onFocus={(e) => {
                    e.target.style.borderColor = currentColors.accent;
                    e.target.style.backgroundColor = currentColors.cardBg;
                    e.target.style.color = currentColors.text;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = currentColors.borderLight;
                    e.target.style.backgroundColor = currentColors.cardBg;
                    e.target.style.color = currentColors.text;
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-btn w-full py-4 font-mono text-sm uppercase tracking-wide font-bold rounded-lg transition-all duration-300 hover:scale-105 flex items-center justify-center gap-3 relative overflow-hidden"
                style={{
                  backgroundColor: currentColors.accent,
                  color: currentColors.bg
                }}
                onMouseEnter={(e) => {
                  anime({
                    targets: e.currentTarget,
                    boxShadow: [`0 4px 20px ${currentColors.accent}40`, `0 8px 32px ${currentColors.accent}80`],
                    duration: 300,
                    easing: 'easeOutQuart'
                  });
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                    <Sparkles size={16} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Enhanced Contact Information */}
          <div ref={infoRef} className="opacity-0">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles size={24} style={{ color: currentColors.accent }} />
              <h3 
                className="font-mono text-lg uppercase tracking-wide font-bold"
                style={{ color: currentColors.text }}
              >
                Get In Touch
              </h3>
            </div>
            
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="space-y-6">
                <div className="contact-item flex items-center gap-6 p-4 rounded-lg transition-all duration-300 hover:scale-105 opacity-0" style={{ backgroundColor: `${currentColors.accent}10` }}>
                  <div 
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${currentColors.accent}20` }}
                  >
                    <Mail size={24} style={{ color: currentColors.accent }} />
                  </div>
                  <div>
                    <div 
                      className="font-mono text-xs uppercase tracking-wide font-semibold mb-1"
                      style={{ color: currentColors.textSecondary }}
                    >
                      Email Address
                    </div>
                    <div 
                      className="text-lg font-semibold"
                      style={{ color: currentColors.text }}
                    >
                      {personalInfo.email}
                    </div>
                  </div>
                </div>

                <div className="contact-item flex items-center gap-6 p-4 rounded-lg transition-all duration-300 hover:scale-105 opacity-0" style={{ backgroundColor: `${currentColors.accent}10` }}>
                  <div 
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${currentColors.accent}20` }}
                  >
                    <MapPin size={24} style={{ color: currentColors.accent }} />
                  </div>
                  <div>
                    <div 
                      className="font-mono text-xs uppercase tracking-wide font-semibold mb-1"
                      style={{ color: currentColors.textSecondary }}
                    >
                      Location
                    </div>
                    <div 
                      className="text-lg font-semibold"
                      style={{ color: currentColors.text }}
                    >
                      {personalInfo.location}
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="contact-item opacity-0">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles size={20} style={{ color: currentColors.accent }} />
                  <div 
                    className="font-mono text-sm uppercase tracking-wide font-bold"
                    style={{ color: currentColors.text }}
                  >
                    Connect Online
                  </div>
                </div>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-4 rounded-full transition-all duration-300 hover:scale-110 group relative overflow-hidden"
                      style={{ 
                        backgroundColor: `${currentColors.borderLight}50`,
                        border: `2px solid ${currentColors.borderLight}`
                      }}
                      title={social.label}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = `${social.color}20`;
                        e.target.style.borderColor = social.color;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = `${currentColors.borderLight}50`;
                        e.target.style.borderColor = currentColors.borderLight;
                      }}
                    >
                      <div style={{ color: currentColors.text }} className="group-hover:scale-110 transition-transform duration-300">
                        {social.icon}
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Enhanced Availability */}
              <div className="contact-item p-6 rounded-xl opacity-0" style={{ backgroundColor: `${currentColors.accent}15`, border: `2px solid ${currentColors.accent}30` }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full animate-pulse"
                      style={{ backgroundColor: currentColors.accent }}
                    />
                    <span 
                      className="font-mono text-sm uppercase tracking-wide font-bold"
                      style={{ color: currentColors.text }}
                    >
                      Current Status
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles size={20} style={{ color: currentColors.accent }} />
                  <span 
                    className="text-lg font-bold"
                    style={{ color: currentColors.text }}
                  >
                    Available for new projects
                  </span>
                </div>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: currentColors.textSecondary }}
                >
                  Open to discussing data analysis projects, web development opportunities, 
                  and cross-platform mobile applications. Let's build something amazing together 
                  with cutting-edge technology and innovative solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;